import type { SyncWorkspaceResult } from "@app/commands/enterpriseCloud/syncWorkspace";
import type { Router } from "@core/Api/Router";
import { useRouter } from "@core/Api/useRouter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import initEnterprise from "@ext/enterprise/utils/initEnterprise";
import { GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import initEnterpriseCloud from "@ext/enterprise-cloud/utils/initEnterpriseCloud";
import { needsGesCloudWorkspaceInit } from "@ext/enterprise-cloud/utils/needsGesCloudWorkspaceInit";
import { useEffect } from "react";

const initEnterpriseCloudIfSignedIn = async (gesCloudUrl: string, router: Router, apiUrlCreator: ApiUrlCreator) => {
	const user = await new GesCloudApi(gesCloudUrl).getUser();
	if (user) await initEnterpriseCloud(router, apiUrlCreator);
};

const syncEnterpriseCloudWorkspace = async (router: Router, apiUrlCreator: ApiUrlCreator) => {
	const res = await FetchService.fetch<SyncWorkspaceResult>(apiUrlCreator.getSyncGesCloudWorkspaceUrl());
	if (!res.ok) return;

	const result = await res.json();
	if (result === "removed") await initEnterpriseCloud(router, apiUrlCreator);
	if (result === "tokenUpdated") await SourceDataService.reload();
};

const useEnterpriseTokenHandler = (isFirstLoad: boolean) => {
	const router = useRouter();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { isReadOnly } = PageDataContextService.value.conf;

	const { isWeb } = usePlatform();
	const { url: gesCloudUrl, enabled: isGesCloudEnabled } = PageDataContextService.value.conf.enterpriseCloud;
	const isGesCloudActive = Boolean(gesCloudUrl && isGesCloudEnabled);
	const shouldInitGesCloud = needsGesCloudWorkspaceInit(isGesCloudActive, WorkspaceService.workspaces());

	// biome-ignore lint/correctness/useExhaustiveDependencies: its ok
	useEffect(() => {
		const oneTimeCode = router.query.oneTimeCode;

		if (isFirstLoad && !isReadOnly) {
			if (shouldInitGesCloud) {
				void initEnterpriseCloudIfSignedIn(gesCloudUrl, router, apiUrlCreator);
			} else if (isWeb && isGesCloudActive) {
				void syncEnterpriseCloudWorkspace(router, apiUrlCreator);
			} else if (oneTimeCode) {
				void router.pushPath("/");
				void initEnterprise(
					apiUrlCreator.getAddEnterpriseWorkspaceUrl(oneTimeCode),
					apiUrlCreator.getCloneEnterpriseCatalogsUrl(),
				);
			}
		}
	}, [isFirstLoad]);
};

export default useEnterpriseTokenHandler;
